'use client';

import { DeploymentLayout } from '@shared/all/domains/spaces';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useRunners } from '../../hooks/useRunners';
import AppLayout from '../../layouts/AppLayout/AppLayout';
import { spaceConfig } from '../../utils/getSpaceConfig';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import Error from '../common/Error';
import RunningJobs from '../common/RunningJobs';
import NewSpace from '../spaces/NewSpace';
import SpaceCard from '../spaces/SpaceCard';
import SpacesList from '../spaces/SpacesList';

const Spaces = () => {
  const { currentUser } = useUser();
  const router = useRouter();
  const [newSpaceModalOpen, setNewSpaceModalOpen] = useState(false);
  const [suggestedSpacesModalOpen, setSuggestedSpacesModalOpen] =
    useState(false);
  // Live jobs OR an online runner: with a runner up but idle the section is
  // still worth showing, since that's where its leftover containers live.
  const { sessions, runners } = useRunners();
  const hasRunnerActivity = sessions.length > 0 || runners.length > 0;

  const mySpacesQuery = useQuery({
    queryKey: ['mySpaces'],
    queryFn: () => unwrapFromResult(client.spaces.my.query({})),
    enabled: Boolean(currentUser.isAuthenticated)
  });

  const suggestedSpacesQuery = useQuery({
    queryKey: ['suggestedSpaces'],
    queryFn: () => unwrapFromResult(client.spaces.suggested.query({}))
  });

  useEffect(() => {
    if (spaceConfig.layoutType === DeploymentLayout.Space) {
      toast('You are not allowed to access this page.', '', 'error');
      router.push('/');
    }
  }, [spaceConfig.layoutType]);
  return (
    <>
      <AppLayout>
        <section className="space-y-4">
          {/* The left rail is hidden below `laptop`, so this page is where the
              jobs panel lives on narrow screens — and the one place that lists
              every board's jobs together. */}
          {hasRunnerActivity && (
            <div className="rounded-lg border border-ds-border-subtle bg-background-secondary/40 p-3">
              <div className="flex items-center gap-2 pb-1">
                <Activity size={18} className="text-primary" />
                <h1 className="text-lg font-semibold">Running jobs</h1>
              </div>
              <RunningJobs />
            </div>
          )}

          {currentUser.isAuthenticated && (
            <div className="flex justify-between p-2">
              <h1 className="text-lg font-semibold">My Spaces</h1>
              <Button
                text="Create"
                onClick={() => setNewSpaceModalOpen(true)}
                variant="SECONDARY"
              />
            </div>
          )}

          {!mySpacesQuery.isLoading && !mySpacesQuery.isError && (
            <div>
              {mySpacesQuery.data?.spaces.map((space) => (
                <SpaceCard
                  key={space.id}
                  avatar={{
                    spaceAvatarKey: space.avatarKey
                  }}
                  href={`/s/${space.spaceSlug}`}
                  name={space.name}
                  description={space.description}
                />
              ))}
              {mySpacesQuery.data?.spaces.length === 0 && (
                <p className="p-4 text-center text-secondary">
                  You haven&apos;t joined any spaces yet.
                </p>
              )}
            </div>
          )}
          <Error
            loading={mySpacesQuery.isLoading}
            error={mySpacesQuery.isError}
            message="An error occurred while loading your spaces. Please try again."
          />

          {!suggestedSpacesQuery.isLoading && !suggestedSpacesQuery.isError && (
            <div>
              <div className="flex items-center justify-between">
                <h1>Suggested Spaces</h1>
                <Button
                  text="View all"
                  onClick={() => setSuggestedSpacesModalOpen(true)}
                  variant="SECONDARY"
                />
              </div>
              <div>
                {suggestedSpacesQuery.data?.spaces.map((space) => (
                  <SpaceCard
                    key={space.id}
                    avatar={{
                      spaceAvatarKey: space.avatarKey
                    }}
                    href={`/s/${space.spaceSlug}`}
                    name={space.name}
                    description={space.description}
                  />
                ))}
                {suggestedSpacesQuery.data?.spaces.length === 0 && (
                  <p className="p-4 text-center text-secondary">
                    No suggested spaces found for you. Check back later.
                  </p>
                )}
              </div>
              <Error
                loading={suggestedSpacesQuery.isLoading}
                error={suggestedSpacesQuery.isError}
                message="An error occurred while loading your suggested spaces. Please try again."
              />
            </div>
          )}
        </section>
      </AppLayout>
      {newSpaceModalOpen && (
        <NewSpace closeHandler={() => setNewSpaceModalOpen(false)} />
      )}
      {suggestedSpacesModalOpen && (
        <SpacesList
          title="Suggested Spaces"
          closeHandler={() => setSuggestedSpacesModalOpen(false)}
          fetchFunction={(cursor) =>
            client.spaces.suggested.query({
              cursor
            })
          }
        />
      )}
    </>
  );
};

export default Spaces;
